import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { redux } from 'Core/redux';
import { AirCompressorsService } from 'Service/api/air-compressors-service';
import { showToast } from 'Core/redux/toast/slice';

export const STORE_KEY = 'airCompressors';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
  },
  selectedAirCompressor: null,
  tileView: true,
  maintenanceRecordsData: [],
  maintenanceRecordData: null,
  maintenanceData: null,
  cellHistoryData: [],
};

// Async thunks
export const fetchAirCompressors = createAsyncThunk(
  'airCompressors/fetchAirCompressors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AirCompressorsService.getAllAirCompressors();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Air Compressors');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Air Compressors');
    }
  },
);

export const addAirCompressor = createAsyncThunk(
  'airCompressors/addAirCompressor',
  async (airCompressorPayload, { rejectWithValue }) => {
    try {
      const response = await AirCompressorsService.saveNewAirCompressor(airCompressorPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Air Compressor');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Air Compressor');
    }
  },
);

// Alias for compatibility
export const addAirCompressors = addAirCompressor;

export const editAirCompressors = createAsyncThunk(
  'airCompressors/editAirCompressors',
  async (airCompressorPayload, { rejectWithValue }) => {
    try {
      const response = await AirCompressorsService.saveNewAirCompressor(airCompressorPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update Air Compressor');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update Air Compressor');
    }
  },
);

export const fetchAirCompressorsWithFilters = () => (dispatch) => {
  dispatch(fetchAirCompressors());
};

// Maintenance Records async thunks
export const fetchMaintenanceRecordsByAirCompressorId = createAsyncThunk(
  'airCompressors/fetchMaintenanceRecordsByAirCompressorId',
  async (airCompressorId) => {
    const { response } = await AirCompressorsService.getMaintenanceRecordsByAirCompressorId(airCompressorId);
    return response;
  }
);

export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'airCompressors/fetchMaintenanceRecordsDetails',
  async (recordId) => {
    const { response } = await AirCompressorsService.getMaintenanceRecordDetails(recordId);
    return response;
  }
);

// Fetch air compressor by asset number for cell history
export const fetchAirCompressorByAssetNumber = createAsyncThunk(
  'airCompressors/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await AirCompressorsService.getAllAirCompressorByAssetNumber(assetNumber);
    return response;
  }
);

export const addAirCompressorMaintenanceRecord = createAsyncThunk(
  'airCompressors/addAirCompressorMaintenanceRecord',
  async (maintenanceData) => {
    const { response } = await AirCompressorsService.addMaintenanceRecord(maintenanceData);
    return response;
  }
);

export const { reducer, actions, caseReducers } = createSlice({
  name: 'airCompressors',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setAirCompressor: (state, action) => {
      state.selectedAirCompressor = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAirCompressors.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAirCompressors.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchAirCompressors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addAirCompressor.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAirCompressor.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const newItem = {
            ...action.payload,
            isNewlyCreated: true,
          };
          state.data = [newItem, ...state.data];
        }
      })
      .addCase(addAirCompressor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editAirCompressors.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAirCompressors.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editAirCompressors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Maintenance Records
      .addCase(fetchMaintenanceRecordsByAirCompressorId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByAirCompressorId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByAirCompressorId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance record details
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add maintenance record
      .addCase(addAirCompressorMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAirCompressorMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = [...state.maintenanceRecordsData, action.payload];
      })
      .addCase(addAirCompressorMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch air compressor by asset number (cell history)
      .addCase(fetchAirCompressorByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAirCompressorByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchAirCompressorByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addAirCompressor.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchAirCompressorsWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Air Compressor saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addAirCompressor.rejected, editAirCompressors.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editAirCompressors.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Air Compressor updated successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setAirCompressor, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;
